/**
 * Step6Optional Component
 *
 * Collects 13 optional fields grouped into collapsible sections to avoid overwhelming users.
 * Header: "Optional: The more you share, the harder we can push for savings."
 *
 * Sections:
 * 1. Current & Target Pricing:
 *    - Current premium amount (number)
 *    - Current premium frequency (select: monthly / six_month / annual)
 *    - Competitive quote amount (number)
 *    - Competitive quote carrier (text)
 *    - Target price (number)
 *
 * 2. Payment & Membership Preferences:
 *    - Payment method checkboxes: autopay, paperless, pay-in-full
 *    - Memberships checkboxes: Costco, AAA, AARP, Military + other text input
 *
 * 3. Driving Record & Safety:
 *    - Driving record (structured: accidents, claims, tickets each with Yes/No toggle + conditional details)
 *    - Safety features checkboxes
 *
 * 4. Additional Details:
 *    - Home ownership (select: own / rent / other)
 *    - Defensive driving course (Yes/No toggle + conditional date input)
 *    - Driver's license number (type=password for masking, optional)
 *    - Email (email input, optional)
 *    - Other insurance policies (checkboxes)
 *    - Vehicle use (select: commute / pleasure / business / rideshare)
 *
 * 5. Consent Section (NOT collapsible):
 *    - Carrier contact authorization checkbox
 *    - Privacy policy agreement checkbox
 *    - Terms of service agreement checkbox
 *
 * 6. Honeypot field:
 *    - Hidden via CSS (not type=hidden) for anti-spam
 *
 * Uses react-hook-form's useFormContext and Controller.
 * Validates against step6Schema from formSchemas.
 */

import { useState, useEffect, type ReactNode } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { AnimatePresence, motion } from 'framer-motion';
import { Emery } from '@/components/ui/Calla';
import { EMERY_MESSAGES } from '@/constants/callaMessages';
import { FormField } from '@/components/ui/FormField';
import { AnimatedReveal } from '@/components/ui/AnimatedReveal';
import { ChevronDown } from 'lucide-react';
import { type FullFormData } from '@/schemas/formSchemas';
import { supabase } from '@/lib/supabase';

export default function Step6Optional() {
  const { control, formState: { errors }, watch, setValue } = useFormContext<FullFormData>();

  // Fetch signed-in user email
  const [userEmail, setUserEmail] = useState('');
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user?.email) setUserEmail(user.email);
    });
  }, []);

  const useSigninEmail = watch('step6.useSigninEmail');

  // Collapsible section state
  const [expandedSections, setExpandedSections] = useState({
    pricing: false,
    payment: false,
    driving: false,
    additional: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Watch for conditional fields
  const hasCompetitiveQuote = watch('step6.hasCompetitiveQuote');
  const defensiveDrivingCompleted = watch('step6.defensiveDrivingCompleted');
  const memberships = watch('step6.memberships');
  const safetyFeatures = watch('step6.safetyFeatures');
  const carrierName = watch('step1.carrierName');
  const carrierNameOther = watch('step1.carrierNameOther');

  const accidentsHasIncident = watch('step6.drivingRecordAccidents.hasIncident');
  const claimsHasIncident = watch('step6.drivingRecordClaims.hasIncident');
  const ticketsHasIncident = watch('step6.drivingRecordTickets.hasIncident');

  return (
    <div>
      <Emery message={EMERY_MESSAGES.step6} />

      {/* ============= TOP-LEVEL REQUIRED FIELDS ============= */}
      <FormField
        label="What is your current premium?"
        name="step6.currentPremiumAmount"
        required={false}
        error={errors.step6?.currentPremiumAmount?.message}
        helperText="Annual or monthly amount in USD (e.g., 1200)"
      >
        <Controller
          name="step6.currentPremiumAmount"
          control={control}
          render={({ field }) => (
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-base" style={{ color: 'var(--text-muted)' }}>$</span>
              <input
                {...field}
                type="text"
                inputMode="decimal"
                pattern="[0-9.]*"
                placeholder="1200"
                className={`form-input pl-8 ${errors.step6?.currentPremiumAmount ? 'form-input-error' : ''}`}
                onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : '')}
              />
            </div>
          )}
        />
      </FormField>

      <FormField
        label="How often do you pay?"
        name="step6.currentPremiumFrequency"
        required={false}
        error={errors.step6?.currentPremiumFrequency?.message}
      >
        <Controller
          name="step6.currentPremiumFrequency"
          control={control}
          render={({ field }) => (
            <select {...field} className={`form-input ${errors.step6?.currentPremiumFrequency ? 'form-input-error' : ''}`}>
              <option value="">How often?</option>
              <option value="monthly">Monthly</option>
              <option value="six_month">6 Months</option>
              <option value="annual">Annual</option>
            </select>
          )}
        />
      </FormField>

      <FormField
        label="Email address"
        name="step6.emailOnFile"
        required={false}
        error={errors.step6?.emailOnFile?.message}
        helperText="We'll send your results here. Without it, we have no way to reach you about savings."
      >
        <Controller
          name="step6.emailOnFile"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="email"
              inputMode="email"
              autoComplete="email"
              enterKeyHint="next"
              data-sensitive=""
              placeholder="your.email@example.com"
              disabled={useSigninEmail}
              className={`form-input ${useSigninEmail ? 'opacity-50 bg-gray-100' : ''} ${errors.step6?.emailOnFile ? 'form-input-error' : ''}`}
            />
          )}
        />
      </FormField>

      {userEmail && (
        <div className="mb-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <Controller
              name="step6.useSigninEmail"
              control={control}
              render={({ field: { value, ...field } }) => (
                <input
                  {...field}
                  type="checkbox"
                  checked={value}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    if (e.target.checked) {
                      setValue('step6.emailOnFile', userEmail);
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
                />
              )}
            />
            <span className="text-sm text-gray-700">Use the email I signed in with</span>
          </label>
        </div>
      )}

      {/* ============= SECTION 1: PRICING ============= */}
      <CollapsibleSection
        title="Pricing"
        isExpanded={expandedSections.pricing}
        onToggle={() => toggleSection('pricing')}
      >
        {/* Competitive Quote Gate */}
        <div className="mb-6">
          <label className="form-label">Have you received a quote from another insurer?</label>
          <div className="mt-3 flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <Controller
                name="step6.hasCompetitiveQuote"
                control={control}
                render={({ field: { value, ...field } }) => (
                  <input
                    {...field}
                    type="radio"
                    value="true"
                    checked={value === true}
                    onChange={() => field.onChange(true)}
                    className="w-4 h-4 text-brand-primary focus:ring-brand-primary cursor-pointer"
                  />
                )}
              />
              <span className="text-sm font-medium text-gray-700">Yes</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Controller
                name="step6.hasCompetitiveQuote"
                control={control}
                render={({ field: { value, ...field } }) => (
                  <input
                    {...field}
                    type="radio"
                    value="false"
                    checked={value === false}
                    onChange={() => field.onChange(false)}
                    className="w-4 h-4 text-brand-primary focus:ring-brand-primary cursor-pointer"
                  />
                )}
              />
              <span className="text-sm font-medium text-gray-700">No</span>
            </label>
          </div>
        </div>

        <AnimatedReveal isVisible={hasCompetitiveQuote}>
          <>
            <FormField
              label="Competitive Quote Amount (Optional)"
              name="step6.competitiveQuoteAmount"
              required={false}
              error={errors.step6?.competitiveQuoteAmount?.message}
              helperText="If you have a competing quote, share the amount"
            >
              <Controller
                name="step6.competitiveQuoteAmount"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-base" style={{ color: 'var(--text-muted)' }}>$</span>
                    <input
                      {...field}
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9.]*"
                      placeholder="1100"
                      className="form-input pl-8"
                      onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : '')}
                    />
                  </div>
                )}
              />
            </FormField>

            <FormField
              label="Competitive Quote Carrier (Optional)"
              name="step6.competitiveQuoteCarrier"
              required={false}
              error={errors.step6?.competitiveQuoteCarrier?.message}
              helperText="Which carrier gave you the quote?"
            >
              <Controller
                name="step6.competitiveQuoteCarrier"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    placeholder="GEICO"
                    className="form-input"
                  />
                )}
              />
            </FormField>
          </>
        </AnimatedReveal>

        <FormField
          label="Target Price (Optional)"
          name="step6.targetPrice"
          required={false}
          error={errors.step6?.targetPrice?.message}
          helperText="The ideal premium you're aiming for"
        >
          <Controller
            name="step6.targetPrice"
            control={control}
            render={({ field }) => (
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-base" style={{ color: 'var(--text-muted)' }}>$</span>
                <input
                  {...field}
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9.]*"
                  placeholder="900"
                  className="form-input pl-8"
                  onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : '')}
                />
              </div>
            )}
          />
        </FormField>
      </CollapsibleSection>

      {/* ============= SECTION 2: PAYMENT & MEMBERSHIP ============= */}
      <CollapsibleSection
        title="Discounts"
        isExpanded={expandedSections.payment}
        onToggle={() => toggleSection('payment')}
      >
        <div className="mb-6">
          <label className="form-label">Payment Method Preferences (Optional)</label>
          <div className="mt-3 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <Controller
                name="step6.hasAutopay"
                control={control}
                render={({ field: { value, ...field } }) => (
                  <input
                    {...field}
                    type="checkbox"
                    checked={value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
                  />
                )}
              />
              <span className="text-sm text-gray-700">Autopay enrolled</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Controller
                name="step6.hasPaperless"
                control={control}
                render={({ field: { value, ...field } }) => (
                  <input
                    {...field}
                    type="checkbox"
                    checked={value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
                  />
                )}
              />
              <span className="text-sm text-gray-700">Paperless billing</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Controller
                name="step6.paysInFull"
                control={control}
                render={({ field: { value, ...field } }) => (
                  <input
                    {...field}
                    type="checkbox"
                    checked={value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
                  />
                )}
              />
              <span className="text-sm text-gray-700">Pay in full annually</span>
            </label>
          </div>
        </div>

        <div className="mb-6">
          <label className="form-label">Memberships & Affiliations (Optional)</label>
          <div className="mt-3 space-y-2">
            {['Costco', 'AAA', 'AARP', 'Military', 'Other'].map((membership) => (
              <label key={membership} className="flex items-center gap-2 cursor-pointer">
                <Controller
                  name="step6.memberships"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      checked={(field.value || []).includes(membership.toLowerCase())}
                      onChange={(e) => {
                        const value = membership.toLowerCase();
                        const currentList = field.value || [];
                        const newList = e.target.checked
                          ? [...currentList, value]
                          : currentList.filter((item) => item !== value);
                        field.onChange(newList);
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
                    />
                  )}
                />
                <span className="text-sm text-gray-700">{membership}</span>
              </label>
            ))}
          </div>
          <AnimatedReveal isVisible={(memberships || []).includes('other')}>
            <FormField
              label="Other Membership"
              name="step6.membershipsOther"
              required={false}
              error={errors.step6?.membershipsOther?.message}
            >
              <Controller
                name="step6.membershipsOther"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    placeholder="e.g., Professional association, Alumni group"
                    className="form-input"
                  />
                )}
              />
            </FormField>
          </AnimatedReveal>
        </div>
      </CollapsibleSection>

      {/* ============= SECTION 3: DRIVING RECORD & SAFETY ============= */}
      <CollapsibleSection
        title="Driving record"
        isExpanded={expandedSections.driving}
        onToggle={() => toggleSection('driving')}
      >
        {/* Accidents */}
        <div className="mb-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <Controller
              name="step6.drivingRecordAccidents.hasIncident"
              control={control}
              render={({ field: { value, ...field } }) => (
                <input
                  {...field}
                  type="checkbox"
                  checked={value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
                />
              )}
            />
            <span className="text-sm font-medium text-gray-700">
              I have had accidents in the last 3 years
            </span>
          </label>
          <AnimatedReveal isVisible={accidentsHasIncident}>
            <FormField
              label="Details about accidents"
              name="step6.drivingRecordAccidents.details"
              required={false}
              error={errors.step6?.drivingRecordAccidents?.details?.message}
            >
              <Controller
                name="step6.drivingRecordAccidents.details"
                control={control}
                render={({ field }) => (
                  <textarea
                    {...field}
                    placeholder="Describe each accident (dates, severity, fault)"
                    className="form-input"
                    rows={3}
                  />
                )}
              />
            </FormField>
          </AnimatedReveal>
        </div>

        {/* Claims */}
        <div className="mb-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <Controller
              name="step6.drivingRecordClaims.hasIncident"
              control={control}
              render={({ field: { value, ...field } }) => (
                <input
                  {...field}
                  type="checkbox"
                  checked={value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
                />
              )}
            />
            <span className="text-sm font-medium text-gray-700">
              I have filed insurance claims
            </span>
          </label>
          <AnimatedReveal isVisible={claimsHasIncident}>
            <FormField
              label="Details about claims"
              name="step6.drivingRecordClaims.details"
              required={false}
              error={errors.step6?.drivingRecordClaims?.details?.message}
            >
              <Controller
                name="step6.drivingRecordClaims.details"
                control={control}
                render={({ field }) => (
                  <textarea
                    {...field}
                    placeholder="Describe each claim (dates, type, amount)"
                    className="form-input"
                    rows={3}
                  />
                )}
              />
            </FormField>
          </AnimatedReveal>
        </div>

        {/* Tickets */}
        <div className="mb-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <Controller
              name="step6.drivingRecordTickets.hasIncident"
              control={control}
              render={({ field: { value, ...field } }) => (
                <input
                  {...field}
                  type="checkbox"
                  checked={value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
                />
              )}
            />
            <span className="text-sm font-medium text-gray-700">
              I have received traffic tickets
            </span>
          </label>
          <AnimatedReveal isVisible={ticketsHasIncident}>
            <FormField
              label="Details about tickets"
              name="step6.drivingRecordTickets.details"
              required={false}
              error={errors.step6?.drivingRecordTickets?.details?.message}
            >
              <Controller
                name="step6.drivingRecordTickets.details"
                control={control}
                render={({ field }) => (
                  <textarea
                    {...field}
                    placeholder="Describe each ticket (dates, violation type)"
                    className="form-input"
                    rows={3}
                  />
                )}
              />
            </FormField>
          </AnimatedReveal>
        </div>

        {/* Safety Features */}
        <div className="mb-6">
          <label className="form-label">Safety Features (Optional)</label>
          <div className="mt-3 space-y-2">
            {['Anti-lock brakes', 'Airbags', 'Electronic stability control', 'Backup camera', 'Blind spot monitoring', 'Other'].map((feature) => (
              <label key={feature} className="flex items-center gap-2 cursor-pointer">
                <Controller
                  name="step6.safetyFeatures"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      checked={(field.value || []).includes(feature.toLowerCase().replace(/\s+/g, '_'))}
                      onChange={(e) => {
                        const value = feature.toLowerCase().replace(/\s+/g, '_');
                        const currentList = field.value || [];
                        const newList = e.target.checked
                          ? [...currentList, value]
                          : currentList.filter((item) => item !== value);
                        field.onChange(newList);
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
                    />
                  )}
                />
                <span className="text-sm text-gray-700">{feature}</span>
              </label>
            ))}
          </div>
          <AnimatedReveal isVisible={(safetyFeatures || []).includes('other')}>
            <FormField
              label="Other Safety Features"
              name="step6.safetyFeaturesOther"
              required={false}
              error={errors.step6?.safetyFeaturesOther?.message}
            >
              <Controller
                name="step6.safetyFeaturesOther"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    placeholder="e.g., Lane departure warning, Dash cam"
                    className="form-input"
                  />
                )}
              />
            </FormField>
          </AnimatedReveal>
        </div>
      </CollapsibleSection>

      {/* ============= SECTION 4: ADDITIONAL DETAILS ============= */}
      <CollapsibleSection
        title="Other details"
        isExpanded={expandedSections.additional}
        onToggle={() => toggleSection('additional')}
      >
        <div className="mb-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <Controller
              name="step6.defensiveDrivingCompleted"
              control={control}
              render={({ field: { value, ...field } }) => (
                <input
                  {...field}
                  type="checkbox"
                  checked={value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
                />
              )}
            />
            <span className="text-sm font-medium text-gray-700">
              I have completed a defensive driving course
            </span>
          </label>
        </div>

        <AnimatedReveal isVisible={defensiveDrivingCompleted}>
          <FormField
            label="Defensive Driving Course Date (Optional)"
            name="step6.defensiveDrivingDate"
            required={false}
            error={errors.step6?.defensiveDrivingDate?.message}
          >
            <Controller
              name="step6.defensiveDrivingDate"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="date"
                  className="form-input"
                />
              )}
            />
          </FormField>
        </AnimatedReveal>

        <FormField
          label="Driver's License Number (Optional)"
          name="step6.driversLicenseNumber"
          required={false}
          sensitive={true}
          error={errors.step6?.driversLicenseNumber?.message}
          helperText="Masked for security"
        >
          <Controller
            name="step6.driversLicenseNumber"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="password"
                autoComplete="off"
                enterKeyHint="done"
                data-sensitive=""
                placeholder="Enter your license number"
                className="form-input"
              />
            )}
          />
        </FormField>

        <div className="mb-6">
          <label className="form-label">
            Do you have any of these other policies with {carrierName === 'Other' ? (carrierNameOther || 'your current carrier') : (carrierName || 'your current carrier')}?
          </label>
          <div className="mt-3 space-y-2">
            {['Homeowners', 'Renters', 'Life Insurance', 'Umbrella'].map((policy) => (
              <label key={policy} className="flex items-center gap-2 cursor-pointer">
                <Controller
                  name="step6.otherPolicies"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      checked={(field.value || []).includes(policy.toLowerCase().replace(/\s+/g, '_'))}
                      onChange={(e) => {
                        const value = policy.toLowerCase().replace(/\s+/g, '_');
                        const currentList = field.value || [];
                        const newList = e.target.checked
                          ? [...currentList, value]
                          : currentList.filter((item) => item !== value);
                        field.onChange(newList);
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
                    />
                  )}
                />
                <span className="text-sm text-gray-700">{policy}</span>
              </label>
            ))}
          </div>
        </div>

      </CollapsibleSection>

      {/* ============= CONSENT SECTION (NOT COLLAPSIBLE) ============= */}
      <div className="mt-12 pt-8 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          Consent & Agreements
        </h3>

        {/* Carrier Contact Authorization */}
        <div className="mb-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <Controller
              name="consent.consentCarrierContact"
              control={control}
              render={({ field: { value, ...field } }) => (
                <input
                  {...field}
                  type="checkbox"
                  checked={value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer mt-1"
                />
              )}
            />
            <span className="text-sm text-gray-700">
              I authorize OneCalla to contact my carrier to review my current policy and identify available discounts.
            </span>
          </label>
          {errors.consent?.consentCarrierContact && (
            <p className="form-error mt-2">{errors.consent.consentCarrierContact.message}</p>
          )}
        </div>

        {/* Privacy Policy Agreement */}
        <div className="mb-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <Controller
              name="consent.consentPrivacyPolicy"
              control={control}
              render={({ field: { value, ...field } }) => (
                <input
                  {...field}
                  type="checkbox"
                  checked={value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer mt-1"
                />
              )}
            />
            <span className="text-sm text-gray-700">
              I agree to the{' '}
              <a href="https://onecalla.com/privacy" className="text-brand-primary hover:underline" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
            </span>
          </label>
          {errors.consent?.consentPrivacyPolicy && (
            <p className="form-error mt-2">{errors.consent.consentPrivacyPolicy.message}</p>
          )}
        </div>

        {/* Terms of Service Agreement */}
        <div className="mb-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <Controller
              name="consent.consentTermsOfService"
              control={control}
              render={({ field: { value, ...field } }) => (
                <input
                  {...field}
                  type="checkbox"
                  checked={value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer mt-1"
                />
              )}
            />
            <span className="text-sm text-gray-700">
              I agree to the{' '}
              <a href="https://onecalla.com/terms" className="text-brand-primary hover:underline" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>
            </span>
          </label>
          {errors.consent?.consentTermsOfService && (
            <p className="form-error mt-2">{errors.consent.consentTermsOfService.message}</p>
          )}
        </div>
      </div>

      {/* ============= HONEYPOT (Hidden via CSS) ============= */}
      <div className="hidden-honeypot">
        <Controller
          name="honeypot.website"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              placeholder="Your website"
              aria-hidden="true"
              tabIndex={-1}
              autoComplete="off"
            />
          )}
        />
      </div>
    </div>
  );
}

interface CollapsibleSectionProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  answered?: number;
  total?: number;
  children: ReactNode;
}

function CollapsibleSection({
  title,
  isExpanded,
  onToggle,
  answered,
  total,
  children,
}: CollapsibleSectionProps) {
  return (
    <div className="mb-8 rounded-xl" style={{ border: "1px solid var(--border-default)" }}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between transition-colors rounded-t-xl"
        style={{ backgroundColor: isExpanded ? "var(--bg-page)" : "transparent" }}
      >
        <h3 className="text-base font-semibold" style={{ color: "var(--text-primary)" }}>{title}</h3>
        <div className="flex items-center gap-3">
          {total != null && answered != null && (
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>
              {answered} of {total} answered
            </span>
          )}
          <ChevronDown
            size={18}
            className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            style={{ color: "var(--text-muted)" }}
          />
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            style={{ overflow: "hidden" }}
          >
            <div className="px-6 py-6 space-y-6" style={{ borderTop: "1px solid var(--border-default)", background: "var(--bg-page)" }}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
